/**
 * @file homeApi.ts
 * @brief Data for the web Home: the same four rails, same calls, as the phone.
 *
 * @details
 * Mirrors what `FRONTEND/src/screens/Home/HomeScreen.tsx` fetches, request for
 * request, so a user sees the same items on both:
 * - Meetings: `GET /boardroom/sessions?page=1&pageSize=N` (RecentMeetings);
 *   N = `rowLimit()`: 5 on the web, up to 12 in the full-width Mac app
 * - Sessions: `GET /sessions?limit=N&employee_id=<me>`; `employee_id` keeps it
 *   personal even for an admin (RecentSessions)
 * - Scheduled: `GET /auth/scheduled-sessions`, filtered to upcoming
 *   (HorizontalScheduledSessions)
 * - Notes: `GET /notes?page=1&pageSize=N&sort=recent` plus
 *   `GET /notes/shared-with-me?limit=2`, merged like `recentNotesMerge.ts`
 *
 * Every rail hides until it has data, as on the phone.
 *
 * @author DES
 * @date 30-09-2026
 */

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, apiPut } from "@/shared/api/client";
import { MeetingsListResponseSchema } from "@/features/company-meetings/schemas";
import { NotesListResponseSchema } from "@/features/notes/schemas";
import { homeRowLimit, mergeRecentNotes, upcomingScheduled, type HomeNote } from "./homeLogic";

export type { HomeNote } from "./homeLogic";

/** Same freshness idea as the phone's 60s throttle: no refetch storms. */
const STALE_MS = 60_000;

/**
 * Items per rail: the 1280px rail, i.e. the phone's 5, on the web and in the
 * Mac app. The Mac app filled the screen's width 19-10 and is back on the
 * centred column 20-10 (DES); homeRowLimit still sizes a wider rail.
 */
function rowLimit(): number {
  if (typeof window === "undefined") return 5;
  return homeRowLimit(Math.min(window.innerWidth, 1280));
}

/** `scope`: the company admin's "Mine | Team" (22-10-2026); omitted for
 *  everyone else. */
export function useHomeMeetings(enabled: boolean, scope?: "mine" | "team") {
  return useQuery({
    queryKey: ["home", "meetings", scope ?? null],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/boardroom/sessions?page=1&pageSize=${rowLimit()}${scope ? `&scope=${scope}` : ""}`,
      );
      return MeetingsListResponseSchema.parse(raw).meetings;
    },
  });
}

const SessionRowSchema = z
  .object({
    session_id: z.string(),
    started_at: z.string().nullable().optional(),
    source_language: z.string().nullable().optional(),
    target_language: z.string().nullable().optional(),
    person_a_name: z.string().nullable().optional(),
    person_b_name: z.string().nullable().optional(),
    total_cents: z.coerce.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    notes: z.string().nullable().optional(),
    // The phone's SessionCard fields (04-10-2026). All are in the SELECT of
    // `GET /api/sessions` (BACKEND/src/features/sessions/routes.js).
    source_locale: z.string().nullable().optional(),
    target_locale: z.string().nullable().optional(),
    duration_seconds: z.coerce.number().nullable().optional(),
    /** Money actually captured by Stripe (`charge_cents > 0`), never the bill. */
    is_paid: z.boolean().nullable().optional(),
    billing_context: z.string().nullable().optional(),
    session_kind: z.string().nullable().optional(),
    person_a_avatar_url: z.string().nullable().optional(),
    person_a_gender: z.string().nullable().optional(),
    person_b_gender: z.string().nullable().optional(),
    /** The session OWNER (`uA`), despite the name: the "For …" row. */
    participant_first_name: z.string().nullable().optional(),
    participant_last_name: z.string().nullable().optional(),
    participant_email: z.string().nullable().optional(),
  })
  .passthrough();
export type HomeSession = z.infer<typeof SessionRowSchema>;

const homeSessionsKey = (userId: string | undefined) => ["home", "sessions", userId] as const;

export function useHomeSessions(userId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: homeSessionsKey(userId),
    enabled: enabled && Boolean(userId),
    staleTime: STALE_MS,
    queryFn: async () => {
      const raw = await apiGet<{ sessions?: unknown[] }>(
        `/api/sessions?limit=${rowLimit()}&employee_id=${encodeURIComponent(userId!)}`,
      );
      return z.array(SessionRowSchema).parse(raw?.sessions ?? []);
    },
  });
}

/**
 * Every tag the caller has used on a session (`available_tags`, ownership
 * scoped, never narrowed by filters): the tag editor's "Your tags", as the
 * phone's TagEditModal fetches it with a 1-row list call.
 */
export function useSessionTagCatalogue(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "sessions", "tag-catalogue"],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const raw = await apiGet<{ available_tags?: unknown }>("/api/sessions?limit=1");
      return z.array(z.string()).catch([]).parse(raw?.available_tags ?? []);
    },
  });
}

const ScheduledRowSchema = z
  .object({
    id: z.union([z.string(), z.number()]).transform(String),
    scheduled_at: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    time_status: z.string().nullable().optional(),
    person_b_name: z.string().nullable().optional(),
    participant_first_name: z.string().nullable().optional(),
    scheduler_first_name: z.string().nullable().optional(),
    scheduler_last_name: z.string().nullable().optional(),
    source_language: z.string().nullable().optional(),
    target_language: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
  })
  .passthrough();
export type HomeScheduled = z.infer<typeof ScheduledRowSchema>;

export function useHomeScheduled(userId: string | undefined, enabled: boolean) {
  return useQuery({
    // Per user, like the sessions rail (01-10-2026).
    queryKey: ["home", "scheduled", userId],
    enabled: enabled && Boolean(userId),
    staleTime: STALE_MS,
    queryFn: async () => {
      const raw = await apiGet<{ sessions?: unknown[] }>("/api/auth/scheduled-sessions");
      return upcomingScheduled(z.array(ScheduledRowSchema).parse(raw?.sessions ?? []));
    },
  });
}

export function useHomeNotes(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "notes"],
    enabled,
    staleTime: STALE_MS,
    queryFn: async () => {
      const limit = rowLimit();
      const [ownRaw, sharedRaw] = await Promise.all([
        apiGet<unknown>(`/api/notes?page=1&pageSize=${limit}&sort=recent`),
        apiGet<{ notes?: HomeNote[] }>("/api/notes/shared-with-me?limit=2").catch(() => ({
          notes: [] as HomeNote[],
        })),
      ]);
      const own = (NotesListResponseSchema.parse(ownRaw).notes ?? []) as HomeNote[];
      const shared = (sharedRaw?.notes ?? []).map((n) => ({ ...n, is_owner: false }));
      return mergeRecentNotes(own, shared, limit);
    },
  });
}

/** Collapsed Home sections, shared with the phone (`PUT /auth/home-preferences`). */
export function saveHomeCollapsed(collapsedSections: string[]) {
  return apiPut("/api/auth/home-preferences", { collapsedSections });
}

/**
 * Owned meetings / sessions of the last 30 days, for the slide button's
 * fallback (GET /api/auth/home-usage, DES 12-10-2026). Best effort: an older
 * server or a failure leaves `data` undefined and the slide uses its default.
 */
export function useHomeUsage(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "usage"],
    queryFn: () => apiGet<{ meetings: number; sessions: number }>("/api/auth/home-usage"),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

